<template>
  <div>
    <div class="newIccStyles" id="contentParent">

      <!-- if "group by title" is disabled, then only one group will exist -->
      <div v-for="group in contentSectionsGrouped" :key="group.title">

        <!-- will NOT be shown for "fake" groups which are those with empty title -->
        <div v-if="group.title" class="d-flex ml-1 mb-4">

          <graphic :displayOnlyTopicIcon="true" :additionalClasses="'d-flex'" :title="group.document"
                   thumbnail width="24px">
          </graphic>

          <h3 class="ml-1 mb-3 black--text">{{ group.title }}</h3>
        </div>

        <content-section
          v-for="contentSection in group.sections"
          :key="contentSection.sectionId"
          @sectionSortOrderChanged="$emit('sectionSortOrderChanged', $event)"
          :is-notes-section-expanded="isNotesSectionExpanded"
          :contentSection="contentSection"
          @breadcrumb-clicked="handleBreadcrumbClick"
          :contentModifiers="contentModifiers"
          :showAllBookmarks="showAllBookmarks"
          :renderToolbar="renderToolbar"
          :showNoteControlMenu="showNoteControlMenu"
          :userBookmarksDefaultSortOrder="getUserBookmarkSortOrderForContentSection(contentSection)"
          :highlightType="highlightType"
          :showTitleAsLink="showTitleAsLink"
          :displayBreadCrumbs="displayBreadCrumbs"
          :displaySectionSeparated="displaySectionSeparated"
        />

      </div>

    </div>

    <template v-if="registerDialogs">
      <peak-ahead/>
      <astm-modal/>
      <note-bookmark-modals/>
      <premium-tools-access-restriction-modal/>
      <toolbar-actions-view-mobile/>
      <access-restricted></access-restricted>
    </template>
  </div>
</template>

<script>
import ContentSection from "./ContentSection.vue";
import ContentSectionType from "./types/contentSectionType";
import PeakAhead from "../../components/titles/peakAhead/index.vue";
import AstmModal from '../modals/astmModal.vue';
import DefaultContentModifier from "./content-modifiers/DefaultContentModifier";
import RemoveContentLinksModifier from "./content-modifiers/RemoveContentLinksModifier";
import ContentUtils from "../titles/inc/contentUtils";
import Constants from "../../constants";
import helper from "../inc/mixins/helper";
import ContentSectionBreadcrumb from "./ContentSectionBreadcrumb.vue";
import PremiumToolsAccessRestrictionModal from "../modals/PremiumToolsAccessRestrictionModal.vue";
import NoteBookmarkModals from "./Dialogs/index.vue";
import ToolbarActionsViewMobile from "./ToolbarActionsViewMobile.vue";
import { ArrayUtils } from "../../util/ArrayUtils";
import AriaFixer from "../../util/areaFixer";
import Graphic from "../titles/cover/Graphic.vue";
import AccessRestricted from "../tags/AccessRestricted.vue";

export default {
  name: 'DefaultContentSectionController',

  components: {
    AccessRestricted,
    Graphic,
    PremiumToolsAccessRestrictionModal,
    ContentSectionBreadcrumb,
    ContentSection,
    NoteBookmarkModals,
    PeakAhead,
    AstmModal,
    ToolbarActionsViewMobile
  },

  props: {
    contentSections: {
      required: true,
      type: Array
    },
    isNotesSectionExpanded: {
      type: Boolean,
      required: false,
      default: false
    },
    showAllBookmarks: {
      type: Boolean,
      default: false
    },
    renderToolbar: {
      type: Boolean,
      default: true
    },
    showNoteControlMenu: {
      type: Boolean,
      default: true
    },
    customUserBookmarksSorOrders: {
      type: Object,
      required: false
    },
    registerDialogs: {
      type: Boolean,
      required: false,
      default: true
    },
    highlightType: {
      type: String,
      required: false,
      default: 'none',
      validator: (value) => ['none', 'add-note', 'insert-into'].includes(value)
    },
    showTitleAsLink: {
      type: Boolean,
      default: true
    },
    displayBreadCrumbs: {
      type: Boolean,
      default: true
    },
    hideContentLinks: {
      type: Boolean,
      default: false
    },
    displaySectionSeparated: {
      type: Boolean,
      default: true
    },
    groupByTitle: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      ariaFixerObserver: null
    };
  },

  computed: {
    contentModifiers() {
      return this.hideContentLinks
        ? [new RemoveContentLinksModifier()]
        : this.getContentModifiers();
    },

    contentSectionsGrouped() {
      if (this.groupByTitle) {
        const grouped = ArrayUtils.groupBy(this.contentSections, 'document.display_title');
        return Object.entries(grouped).map(([title, sections]) => ({
          title,
          document: sections?.[0]?.document,
          sections
        }));
      }

      return [{
        title: '',
        document: this.contentSections?.[0]?.document,
        sections: this.contentSections
      }];
    }
  },

  methods: {
    getUserBookmarkSortOrderForContentSection(contentSection) {
      return this.customUserBookmarksSorOrders?.[contentSection.sectionId] || 'desc';
    },

    handleBreadcrumbClick(breadcrumbItem) {
      // TODO: Add analytics or tracking here
    },

    getContentModifiers() {
      //TODO:: this should be refactored
      const defaultContentModifier = new DefaultContentModifier(Routing, this.$http, ContentUtils, helper, Constants, _);
      defaultContentModifier.setRestrictions(this.contentSections?.[0]?.contentRestrictions || {});

      return [defaultContentModifier];
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.ariaFixerObserver = AriaFixer.initAriaFixer(this.$el);
    });
  },

  beforeDestroy() {
    if (this.ariaFixerObserver) {
      this.ariaFixerObserver.disconnect();
    }
  }
};
</script>